import { FormInput, FormPhoneInput, FormSelect, Heading, Loader, PropertyCardHeader, } from "@/components";
import useAddEditProperty from "./useAddEditProperty";
import { CircleCheck, Home, MapPin, Network, Phone, Sparkles, Star } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { ADDRESS, ADDRESS_1, ADDRESS_2, CATEGORY, CITY, COMMUNITY_NAME, DISPLAY_ID, EMERGENCY_CONTACTS, INSURANCE_TYPE, NO_OF_GARAGES, PIN, POC_EMAIL, POC_MOB_NO, POC_NAME, PROPERTY_BLOCK, PROPERTY_MODEL, PROPERTY_NAME, PROPERTY_SIZE, PROPERTY_ZONE, STATE, UNIT_NUMBER, ZIPCODE } from "./consts";
import { Button } from "@/components/ui";

const AddEditProperty = () => {
  const {
    isView,
    form,
    isDetailLoading,
    breadcrumbData,
    requiredFields,
    propertyZones,
    propertyBlock,
    key,
    propertyId,
    displayHierarchy,
    insuranceType,
    propertyCategories,
    propertyModels,
    emergeCount,
    lastInputRef,
    zoneName,
    blockName,
    onCancel,
    stateKey,
    isInsuranceLoading,
    isBlockLoading,
    isCategoryLoading,
    isModelLoading,
    isZoneLoading,
    stateList
  } = useAddEditProperty();
  const {errors} = form.formState;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between">
        <Heading
          title={breadcrumbData[breadcrumbData?.length - 1]?.name || ''}
        />
      </div>

      {isDetailLoading || isInsuranceLoading || isBlockLoading ||
        isCategoryLoading ||
        isModelLoading ||
        isZoneLoading ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 py-[25px] gap-5">
              <div className="lg:col-span-2 bg-white rounded-lg py-[25px] px-[20px]">
                <div className="flex flex-col gap-[30px]">
                  <PropertyCardHeader Icon={Network} title="Hierarchy & Identification" desc="Select the hierarchy first so the property becomes reporting-ready & financially taggable." />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px]">
                    <FormInput
                      name={COMMUNITY_NAME}
                      control={form.control}
                      label="Community"
                      placeholder="Please enter"
                      error={errors[COMMUNITY_NAME]?.message}
                      isRequired={requiredFields.includes(COMMUNITY_NAME)}
                      disabled
                    />
                    <FormInput
                      name={PROPERTY_NAME}
                      control={form.control}
                      label="Property Name"
                      placeholder="Please enter"
                      error={errors[PROPERTY_NAME]?.message}
                      isRequired={requiredFields.includes(PROPERTY_NAME)}
                      disabled={isView}
                    />
                    <FormSelect
                      key={key?.propertyZones}
                      name={PROPERTY_ZONE}
                      control={form.control}
                      label="Property Zone"
                      placeholder="Please select"
                      error={errors[PROPERTY_ZONE]?.message}
                      isRequired={requiredFields.includes(PROPERTY_ZONE)}
                      options={(propertyZones?.data || [])?.map((val: any) => ({
                        label: val?.zoneName,
                        value: val?.zoneId?.toString(),
                      }))}
                      disabled={isView}
                    />
                    <FormSelect
                      key={key?.propertyBlock}
                      name={PROPERTY_BLOCK}
                      control={form.control}
                      label="Property Block"
                      placeholder="Please select"
                      error={errors[PROPERTY_BLOCK]?.message}
                      isRequired={requiredFields.includes(PROPERTY_BLOCK)}
                      options={(propertyBlock?.data || [])?.map((val: any) => ({
                        label: val?.blockName,
                        value: val?.blockId?.toString(),
                      }))}
                      disabled={isView}
                    />
                  </div>
                  {zoneName && blockName ? <div className="border-1 border-blue-100 rounded-lg bg-blue-50 flex flex-col md:flex-row md:items-center md:justify-between p-4">
                    <div className="flex flex-col">
                      <span className="text-[14px] font-medium">Hierarchy Path</span>
                      <span className="text-[12px]">{displayHierarchy}</span>
                    </div>
                    <div className="text-primary text-[12px] flex gap-2 items-center">
                      <CircleCheck /> Zone & Block are hierarchy validated
                    </div>
                  </div> : <></>}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px]">
                    <FormInput
                      name={UNIT_NUMBER}
                      control={form.control}
                      label="Unit Number"
                      placeholder="Please enter"
                      error={errors[UNIT_NUMBER]?.message}
                      isRequired={requiredFields.includes(UNIT_NUMBER)}
                      disabled={isView}
                      variant="integer"
                      minLength={3}
                      maxLength={10}
                    />
                    <div className="lg:col-span-2">
                      {propertyId && (
                        <FormInput
                          name={DISPLAY_ID}
                          control={form.control}
                          label="Property ID"
                          placeholder="Please enter"
                          error={errors[DISPLAY_ID]?.message}
                          isRequired={requiredFields.includes(DISPLAY_ID)}
                          disabled
                        />
                      )}
                    </div>
                  </div>
                </div>
                <hr className="my-6" />
                <div className="flex flex-col gap-[30px]">
                  <PropertyCardHeader Icon={Home} title="Property Attributes" desc="Capture property characteristics while preserving consistency with the hierarchy master." />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px]">
                    <FormInput
                      name={PIN}
                      control={form.control}
                      label="PIN"
                      placeholder="Please enter"
                      error={errors[PIN]?.message}
                      isRequired={requiredFields.includes(PIN)}
                      disabled={isView}
                      maxLength={15}
                    />
                    <FormSelect
                      key={key?.insuranceType}
                      name={INSURANCE_TYPE}
                      control={form.control}
                      label="Insurance Type"
                      placeholder="Please select"
                      error={errors[INSURANCE_TYPE]?.message}
                      isRequired={requiredFields.includes(INSURANCE_TYPE)}
                      options={(insuranceType?.data || [])?.map((val: any) => ({
                        label: val?.insuranceType,
                        value: val?.insuranceTypeId?.toString(),
                      }))}
                      disabled={isView}
                    />
                    <FormSelect
                      key={key?.propertyCategories}
                      name={CATEGORY}
                      control={form.control}
                      label="Property Category"
                      placeholder="Please select"
                      error={errors[CATEGORY]?.message}
                      isRequired={requiredFields.includes(CATEGORY)}
                      options={(propertyCategories?.data || [])?.map(
                        (val: any) => ({
                          label: val?.categoryName,
                          value: val?.categoryId?.toString(),
                        })
                      )}
                      disabled={isView}
                    />
                    <FormSelect
                      key={key?.propertyModels}
                      name={PROPERTY_MODEL}
                      control={form.control}
                      label="Property Model"
                      placeholder="Please select"
                      error={errors[PROPERTY_MODEL]?.message}
                      isRequired={requiredFields.includes(PROPERTY_MODEL)}
                      options={(propertyModels?.data || [])?.map((val: any) => ({
                        label: val?.modelName,
                        value: val?.modelId?.toString(),
                      }))}
                      disabled={isView}
                    />
                    <FormInput
                      name={PROPERTY_SIZE}
                      control={form.control}
                      label="Property Size(SFT)"
                      placeholder="Please enter"
                      error={errors[PROPERTY_SIZE]?.message}
                      isRequired={requiredFields.includes(PROPERTY_SIZE)}
                      disabled={isView}
                      variant="float"
                    />
                    <FormInput
                      name={NO_OF_GARAGES}
                      control={form.control}
                      label="Number of Garages"
                      placeholder="Please enter"
                      error={errors[NO_OF_GARAGES]?.message}
                      isRequired={requiredFields.includes(NO_OF_GARAGES)}
                      disabled={isView}
                      variant="integer"
                    />
                  </div>
                </div>
                <hr className="my-6" />
                <div className="flex flex-col gap-[30px]">
                  <PropertyCardHeader Icon={MapPin} title="Property Address" desc="Keep address details structured for correspondence, reporting , and future geo - based search" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px]">
                    <FormInput
                      name={`${ADDRESS}.${ADDRESS_1}`}
                      control={form.control}
                      label="Address 1"
                      placeholder="Please enter"
                      error={
                        (errors as any)[`${ADDRESS}`]?.[`${ADDRESS_1}`]?.message
                      }
                      isRequired={requiredFields.includes(
                        `${ADDRESS}.${ADDRESS_1}`
                      )}
                      disabled={isView}
                    />
                    <FormInput
                      name={`${ADDRESS}.${ADDRESS_2}`}
                      control={form.control}
                      label="Address 2"
                      placeholder="Please enter"
                      error={
                        (errors as any)[`${ADDRESS}`]?.[`${ADDRESS_2}`]?.message
                      }
                      isRequired={requiredFields.includes(
                        `${ADDRESS}.${ADDRESS_2}`
                      )}
                      disabled={isView}
                    />
                    <FormSelect
                      key={stateKey}
                      name={`${ADDRESS}.${STATE}`}
                      control={form.control}
                      label="State"
                      placeholder="Please select"
                      error={(errors as any)[`${ADDRESS}`]?.[`${STATE}`]?.message}
                      isRequired={requiredFields.includes(`${ADDRESS}.${STATE}`)}
                      options={stateList?.map((data: any) => ({
                        label: data.name,
                        value: data.code?.toString(),
                      }))}
                      disabled={isView}
                    />
                    <FormInput
                      name={`${ADDRESS}.${CITY}`}
                      control={form.control}
                      label="City"
                      placeholder="Please select"
                      error={(errors as any)[`${ADDRESS}`]?.[`${CITY}`]?.message}
                      isRequired={requiredFields.includes(`${ADDRESS}.${CITY}`)}
                      disabled={isView}
                      variant="char"
                    />
                    <FormInput
                      name={`${ADDRESS}.${ZIPCODE}`}
                      control={form.control}
                      label="Zipcode"
                      placeholder="Please enter"
                      error={
                        (errors as any)[`${ADDRESS}`]?.[`${ZIPCODE}`]?.message
                      }
                      isRequired={requiredFields.includes(
                        `${ADDRESS}.${ZIPCODE}`
                      )}
                      maxLength={10}
                      minLength={10}
                      disabled={isView}
                      variant="integerSpeChar"
                    />
                  </div>
                </div>
                <hr className="my-6" />
                <div className="flex flex-col gap-[30px]">
                  <PropertyCardHeader Icon={Phone} title="Emergency Contact Info." desc="You can maintain one or more points of contact without changing the main property structure." />
                  <div className="grid grid-cols-1 gap-[20px]">
                    {Array(emergeCount)
                      .fill(0)
                      .map((_, index) => (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] items-center">
                          <FormInput
                            ref={index == emergeCount - 1 ? lastInputRef : null}
                            name={`${EMERGENCY_CONTACTS}.${index}.${POC_NAME}`}
                            control={form.control}
                            label="POC Name"
                            placeholder="Please enter"
                            isRequired={index == 0}
                            disabled={isView}
                          />
                          <FormPhoneInput
                            name={`${EMERGENCY_CONTACTS}.${index}.${POC_MOB_NO}`}
                            control={form.control}
                            label="POC Mobile No."
                            placeholder="Please enter"
                            isRequired={
                              index == 0 ||
                              form.watch(
                                `${EMERGENCY_CONTACTS}.${index}.${POC_NAME}`
                              )
                            }
                            disabled={isView}
                            error={
                              (errors[EMERGENCY_CONTACTS] as any)?.[`${index}`]?.[
                                POC_MOB_NO
                              ]?.message
                            }
                          />
                          <FormInput
                            name={`${EMERGENCY_CONTACTS}.${index}.${POC_EMAIL}`}
                            control={form.control}
                            label="POC Email"
                            placeholder="Please enter"
                            isRequired={
                              index == 0 ||
                              form.watch(
                                `${EMERGENCY_CONTACTS}.${index}.${POC_NAME}`
                              )
                            }
                            disabled={isView}
                            error={
                              (errors[EMERGENCY_CONTACTS] as any)?.[`${index}`]?.[
                                POC_EMAIL
                              ]?.message
                            }
                          />
                        </div>
                      ))}
                  </div>
                  {errors[EMERGENCY_CONTACTS]?.message && (
                    <p className="text-red-500 text-[10px] mt-1">
                      {errors[EMERGENCY_CONTACTS]?.message as string}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg py-[25px] px-[20px]">
                <div className="flex flex-col gap-[30px]">
                  <PropertyCardHeader Icon={Sparkles} title="Smart Mapping Preview" desc="This property will later be available as a financial dimension in Unified Journal Entry and Cost analytics." />

                  {/* <div className="bg-primary rounded-lg p-4 text-white flex flex-col">
                    <span className="text-[12px] font-[300] opacity-[0.8]">Generated Property ID</span>
                    <span className="text-[14px]">{generatedId}</span>
                  </div> */}

                  <div className="flex flex-col border-1 p-4 rounded-lg gap-[10px]">
                    <div className="flex flex-row items-center justify-between">
                      <span className="text-[12px]">Community</span>
                      <span className="text-[14px] font-medium">{form.watch(COMMUNITY_NAME)}</span>
                    </div>
                    <div className="flex flex-row items-center justify-between">
                      <span className="text-[12px]">Zone</span>
                      <span className="text-[14px] font-medium">{zoneName}</span>
                    </div>
                    <div className="flex flex-row items-center justify-between">
                      <span className="text-[12px]">Block</span>
                      <span className="text-[14px] font-medium">{blockName}</span>
                    </div>
                    <div className="flex flex-row items-center justify-between">
                      <span className="text-[12px]">Property Type</span>
                      <span className="text-[14px] font-medium">Pending</span>
                    </div>
                  </div>

                  {/* <div className="border-1 border-blue-100 rounded-lg bg-blue-50 flex flex-col p-4">
                    <span className="text-[12px]">Generated Property ID</span>
                    <span className="text-[14px] font-medium">{zoneName && blockName ? `${form.watch(COMMUNITY_NAME)}/${zoneName}/${blockName}/${generatedId}` : '-'}</span>
                  </div> */}
                </div>

                <div className="flex flex-col gap-[30px] mt-[25px]">
                  <PropertyCardHeader Icon={Sparkles} title="Posting Governance" desc="Small addition, big product value." />

                  <div className="bg-primary p-4 text-white text-[12px] flex flex-row gap-2 items-start rounded-lg">
                    <Star className="w-9 h-auto"/>
                    <span>Once the first financial posting starts for this property, hierarchy-linked master fields such as Community, Zone, Block, and Property ID should become non-editable.</span>
                  </div>

                  <div className="flex flex-col gap-[15px]">
                    <div className="border-1 p-4 flex flex-row items-center justify-between rounded-lg text-[12px]">
                      <span>Hierarchy validated</span>
                      <CircleCheck className="h-4 w-4 stroke-primary"/>
                    </div>

                    <div className="border-1 p-4 flex flex-row items-center justify-between rounded-lg text-[12px]">
                      <span>Duplicate Property ID check</span>
                      <CircleCheck className="h-4 w-4 stroke-primary"/>
                    </div>

                    <div className="border-1 p-4 flex flex-row items-center justify-between rounded-lg text-[12px]">
                      <span>Unified Journal Entry dimension - ready</span>
                      <CircleCheck className="h-4 w-4 stroke-primary"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row justify-end gap-3  py-3">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export default AddEditProperty;
